// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCache } from '../../../DataCache';
import { Account } from '../../../cache/Account';
import { AccountFeature } from '@shared/utils/Account/AccountFeature';
import { AccountType } from '@shared/utils/cache/Accounts/Account.types';
import { TodayResultsGroupEnum } from '@shared/utils/Enums/AccountDetailsEnum';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { BaseAccountGroupController } from '../BaseControllers/BaseAccountGroupController';
import { RowItemDataNumberArray, RowItemDataStringArray } from '../../../cache/VerticalPanel/AccountDetails/RowItemData';
import { VerticalPanelColoringTypes } from '../../VerticalPanelColoringTypes';
import { GROUP } from '@shared/utils/Rules/AccountRules';
export class TodayResultGroupController extends BaseAccountGroupController {
    getGroupItemsTypes() {
        return [
            TodayResultsGroupEnum.GrossPNL,
            TodayResultsGroupEnum.BeginBalance,
            TodayResultsGroupEnum.TodayFees,
            TodayResultsGroupEnum.TodayVolume,
            TodayResultsGroupEnum.TodayTrades,
            TodayResultsGroupEnum.TodayTurnover,
            TodayResultsGroupEnum.TodayRebates,
            TodayResultsGroupEnum.TodaySwaps
        ];
    }
    getDynamicItems() {
        return [];
    }
    getItemValue(type) {
        const curAccount = this.Account;
        const assetBalance = this.AssetBalance;
        // const useAccCurrency = true; // TODO remove
        switch (type) {
            case TodayResultsGroupEnum.GrossPNL:
                return new RowItemDataNumberArray(assetBalance.TodayGrossPNL);
            case TodayResultsGroupEnum.BeginBalance:
                return new RowItemDataNumberArray(assetBalance.TodayRealizedPNL);
            case TodayResultsGroupEnum.TodayFees:
                return new RowItemDataNumberArray(assetBalance.TodayFees);
            case TodayResultsGroupEnum.TodayVolume:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.TodayVolume, curAccount, assetBalance));
            case TodayResultsGroupEnum.TodayTrades:
                return new RowItemDataNumberArray(Account.GetAccountFeature(AccountFeature.TodayTrades, curAccount, assetBalance));
            case TodayResultsGroupEnum.TodayTurnover:
                return new RowItemDataNumberArray(assetBalance.TodayTurnover);
            case TodayResultsGroupEnum.TodayRebates:
                return new RowItemDataNumberArray(assetBalance.TodayRebates);
            case TodayResultsGroupEnum.TodaySwaps:
                return new RowItemDataNumberArray(assetBalance.TodaySwaps);
        }
    }
    getItemFormattedValue(type, key) {
        const curAccount = this.Account;
        const assetBalance = this.AssetBalance;
        const value = this.getItemValue(type);
        switch (type) {
            case TodayResultsGroupEnum.GrossPNL:
                return new RowItemDataStringArray(assetBalance.formatPriceExactly(value.firstValue));
            case TodayResultsGroupEnum.BeginBalance:
                return new RowItemDataStringArray(assetBalance.formatPriceExactly(value.firstValue));
            case TodayResultsGroupEnum.TodayFees:
                return new RowItemDataStringArray(assetBalance.formatPriceExactly(value.firstValue));
            case TodayResultsGroupEnum.TodayVolume:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.TodayVolume, curAccount, assetBalance));
            case TodayResultsGroupEnum.TodayTrades:
                return new RowItemDataStringArray(Account.GetAccountFeatureString(value.firstValue, AccountFeature.TodayTrades, curAccount, assetBalance));
            case TodayResultsGroupEnum.TodayTurnover:
                if (curAccount.AccountType !== AccountType.MultiAsset) {
                    return new RowItemDataStringArray('');
                }
                else {
                    return new RowItemDataStringArray(assetBalance.formatPrice(assetBalance.TodayTurnover));
                }
            case TodayResultsGroupEnum.TodayRebates:
                return new RowItemDataStringArray(assetBalance.formatPriceExactly(value.firstValue));
            case TodayResultsGroupEnum.TodaySwaps:
                return new RowItemDataStringArray(assetBalance.formatPriceExactly(assetBalance.TodaySwaps));
        }
    }
    getItemNameKey(type) {
        switch (type) {
            case TodayResultsGroupEnum.GrossPNL:
                return 'panel.accounts.GrossPNL';
            case TodayResultsGroupEnum.BeginBalance:
                return 'panel.accounts.BeginBalance';
            case TodayResultsGroupEnum.TodayFees:
                return 'panel.accounts.TodayFees';
            case TodayResultsGroupEnum.TodayVolume:
                return 'panel.accounts.TodayVolume';
            case TodayResultsGroupEnum.TodayTrades:
                return 'panel.accounts.TodayTrades';
            case TodayResultsGroupEnum.TodayTurnover:
                return 'panel.accounts.TodayTurnover';
            case TodayResultsGroupEnum.TodayRebates:
                return 'panel.accounts.TodayRebates';
            case TodayResultsGroupEnum.TodaySwaps:
                return 'panel.accounts.TodaySwaps';
        }
    }
    getItemRule(type) {
        switch (type) {
            case TodayResultsGroupEnum.GrossPNL:
                return 'FUNCTION_ACCOUNT_DETAILS_TODAYS_GROSS';
            case TodayResultsGroupEnum.BeginBalance:
                return 'FUNCTION_ACCOUNT_DETAILS_TODAYS_NET';
            case TodayResultsGroupEnum.TodayFees:
                return 'FUNCTION_ACCOUNT_DETAILS_TODAYS_FEES';
            case TodayResultsGroupEnum.TodayVolume:
                return 'FUNCTION_ACCOUNT_DETAILS_TODAYS_VOLUME';
            case TodayResultsGroupEnum.TodayTrades:
                return 'FUNCTION_ACCOUNT_DETAILS_TODAYS_TRADES';
            case TodayResultsGroupEnum.TodayRebates:
                return 'FUNCTION_ACCOUNT_DETAILS_TODAYS_REBATES';
            case TodayResultsGroupEnum.TodaySwaps:
                return 'FUNCTION_ACCOUNT_DETAILS_TODAYS_SWAPS';
            default:
                return null;
        }
    }
    getItemGroupRule() {
        return GROUP.TODAY_RESULTS;
    }
    isHiddenItemRow(type) {
        if (super.isHiddenItemRow(type)) {
            return true;
        }
        if (type !== TodayResultsGroupEnum.BeginBalance) {
            if (DataCache.isAllowedForMyUser(RulesSet.FUNCTION_EXT_RISK)) {
                return true;
            }
        }
        const account = this.Account;
        const dc = DataCache;
        if (account && dc) {
            if (type === TodayResultsGroupEnum.TodayTurnover) {
                // #33209
                return account.AccountType !== AccountType.MultiAsset;
            }
        }
        else {
            if (type === TodayResultsGroupEnum.TodayTurnover) {
                // #33209, #33495
                let hasMultiAcc = false;
                const accDict = dc.getAccounts();
                for (const accId in accDict) {
                    const acc = accDict[accId];
                    if (acc.AccountType === AccountType.MultiAsset) {
                        hasMultiAcc = true;
                        break;
                    }
                }
                return !hasMultiAcc;
            }
        }
    }
    getColoringMethod(type) {
        return VerticalPanelColoringTypes.Default;
    }
}
